import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

import DefaultLayout from "/home/runner/work/documentation/documentation/node_modules/gatsby-theme-doctornpm/src/components/layout.js";
export const _frontmatter = {};
const layoutProps = {
  _frontmatter
};
const MDXLayout = DefaultLayout;
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <h2>{`See what version of npm you're running`}</h2>
    <pre><code parentName="pre" {...{}}>{`npm -v
`}</code></pre>
    <h2>{`Upgrading on `}<inlineCode parentName="h2">{`*nix`}</inlineCode>{` (OSX, Linux, etc.)`}</h2>
    <p><em parentName="p">{`(You may need to prefix these commands with `}<inlineCode parentName="em">{`sudo`}</inlineCode>{`, especially on Linux, or OS X if you installed Node using its default installer.)`}</em></p>
    <p>{`You can upgrade to the latest version of npm using:`}</p>
    <pre><code parentName="pre" {...{}}>{`npm install -g npm@latest
`}</code></pre>
    <h2>{`Upgrading on Windows`}</h2>
    <p>{`By default, npm is installed alongside node in`}</p>
    <p><inlineCode parentName="p">{`C:\\Program Files (x86)\\nodejs`}</inlineCode></p>
    <p>{`npm's globally installed packages (including, potentially, npm itself) are stored separately in a user-specific directory (which is currently`}</p>
    <p>{` `}<inlineCode parentName="p">{`C:\\Users\\<username>\\AppData\\Roaming\\npm`}</inlineCode>{`).`}</p>
    <p>{` Because the installer puts`}</p>
    <p>{` `}<inlineCode parentName="p">{`C:\\Program Files (x86)\\nodejs`}</inlineCode>{`  `}</p>
    <p>{` before`}</p>
    <p>{` `}<inlineCode parentName="p">{`C:\\Users\\<username>\\AppData\\Roaming\\npm`}</inlineCode></p>
    <p>{` on your `}<inlineCode parentName="p">{`PATH`}</inlineCode>{`, it will always use the version of npm installed with node instead of the version of npm you installed using `}<inlineCode parentName="p">{`npm -g install npm@<version>`}</inlineCode>{`.`}</p>
    <p>{` To get around this, you can do `}<strong parentName="p">{`one`}</strong>{` of the following:`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Option 1: `}<a parentName="p" {...{
            "href": "http://superuser.com/questions/284342/what-are-path-and-other-environment-variables-and-how-can-i-set-or-use-them"
          }}>{`edit your Windows installation's `}<inlineCode parentName="a">{`PATH`}</inlineCode></a>{` to put `}<inlineCode parentName="p">{`%appdata%\\npm`}</inlineCode>{` before `}<inlineCode parentName="p">{`%ProgramFiles%\\nodejs`}</inlineCode>{`.
Remember that you'll need to restart `}<inlineCode parentName="p">{`cmd.exe`}</inlineCode>{` (and potentially restart Windows) when you make changes to `}<inlineCode parentName="p">{`PATH`}</inlineCode>{` or how npm is installed.`}</p>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Option 2: remove both of`}</p>
        <ul parentName="li">
          <li parentName="ul"><inlineCode parentName="li">{`%ProgramFiles%\\nodejs\\npm`}</inlineCode></li>
          <li parentName="ul"><inlineCode parentName="li">{`%ProgramFiles%\\nodejs\\npm.cmd`}</inlineCode></li>
        </ul>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Option 3: Navigate to `}<inlineCode parentName="p">{`%ProgramFiles%\\nodejs\\node_modules\\npm`}</inlineCode>{` and copy the `}<inlineCode parentName="p">{`npmrc`}</inlineCode>{`file to another folder or the desktop.
Then open `}<inlineCode parentName="p">{`cmd.exe`}</inlineCode>{` as an administrator and run the following commands:`}</p>
      </li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-bash"
      }}>{`cd %ProgramFiles%\\nodejs
npm install npm@latest
`}</code></pre>
    <p>{`If you installed npm with the node.js installer, after doing one of the previous steps, do the following.`}</p>
    <ul>
      <li parentName="ul">
        <p parentName="li">{`Option 1 or 2`}</p>
        <ul parentName="li">
          <li parentName="ul">{`Go into `}<inlineCode parentName="li">{`%ProgramFiles%\\nodejs\\node_modules\\npm`}</inlineCode>{` and copy the file named `}<inlineCode parentName="li">{`npmrc`}</inlineCode>{` in the new npm folder, which should be `}<inlineCode parentName="li">{`%appdata%\\npm\\node_modules\\npm`}</inlineCode>{`. This will tell the new npm where the global installed packages are.`}</li>
        </ul>
      </li>
      <li parentName="ul">
        <p parentName="li">{`Option 3`}</p>
        <ul parentName="li">
          <li parentName="ul">{`Copy the npmrc file back into `}<inlineCode parentName="li">{`%ProgramFiles%\\nodejs\\node_modules\\npm`}</inlineCode></li>
        </ul>
      </li>
    </ul>
    <p><em parentName="p">{`(See also the `}<a parentName="em" {...{
          "href": "https://docs.npmjs.com/common-errors#error-enoent-stat-cusersuserappdataroamingnpm-on-windows-7"
        }}>{`point below`}</a>{` if you're running Windows 7 and don't have the directory `}<inlineCode parentName="em">{`%appdata%\\npm`}</inlineCode>{`.)`}</em></p>
    <h3>{`A brief note on the built-in Windows configuration`}</h3>
    <p>{`The Node installer installs, directly into the npm folder, a special piece of Windows-specific configuration that tells npm where to install global packages. When npm is used to install itself, it is supposed to copy this special `}<inlineCode parentName="p">{`builtin`}</inlineCode>{` configuration into the new install. There was a bug in some versions of npm that kept this from working, so you may need to go in and fix that up by hand. Run the following command to see where npm will  install global packages to verify it is correct.`}</p>
    <pre><code parentName="pre" {...{}}>{`npm config get prefix -g
`}</code></pre>
    <p>{`If it isn't set to `}<inlineCode parentName="p">{`<X>:\\Users\\<user>\\AppData\\Roaming\\npm`}</inlineCode>{`, you can run the below command to correct it:`}</p>
    <pre><code parentName="pre" {...{}}>{`npm config set prefix %APPDATA%\\npm -g
`}</code></pre>
    <p>{`Incidentally, if you would prefer that packages not be installed to your roaming profile (because you have a quota on your shared network, or it makes logging in or out from a domain sluggish), you can put it in your local app data instead:`}</p>
    <pre><code parentName="pre" {...{}}>{`npm config set prefix %LOCALAPPDATA%\\npm -g
`}</code></pre>
    <p>{`...as well as copying `}<inlineCode parentName="p">{`%APPDATA%\\npm`}</inlineCode>{` to `}<inlineCode parentName="p">{`%LOCALAPPDATA%\\npm`}</inlineCode>{` (and updating your `}<inlineCode parentName="p">{`%PATH%`}</inlineCode>{`, of course).`}</p>
    <p>{`Everyone who works on npm knows that this process is complicated and fraught, and we're working on making it simpler. Stay tuned.`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      